/**
 * KB용 Google Drive 설정 팝업 모달
 * 워크플로우의 GoogleDriveConfigModal과 동일한 UX, kb-google-drive 커넥터 사용
 */

import React, { useState, useEffect } from 'react';
import connectorApi from '../../api/connectors';
import { Button, Modal, useToast } from '../ui';
import googleDriveLogo from '../../assets/icons/marketplace/google-drive.svg';
import CopyIcon from '../../assets/icons/common/copy.svg?react';
import PlusIcon from '../../assets/icons/common/plus.svg?react';
import EditIcon from '../../assets/icons/common/edit.svg?react';
import UploadIcon from '../../assets/icons/common/upload.svg?react';
import RefreshIcon from '../../assets/icons/common/refresh.svg?react';

// 리디렉션 URI: 운영환경은 같은 origin, 개발환경은 백엔드 포트(3002)로 변경
const getKbDriveRedirectUri = () => {
  const origin = window.location.origin;
  // 개발환경 (Vite dev server 3003 → 백엔드 3002)
  if (origin.includes('localhost:3003') || origin.includes('127.0.0.1:3003')) {
    return origin.replace(':3003', ':3002') + '/api/auth/kb-google-drive/callback';
  }
  // 개발환경 (Vite dev server 5173 → 백엔드 3002)
  if (origin.includes('localhost:5173') || origin.includes('127.0.0.1:5173')) {
    return origin.replace(':5173', ':3002') + '/api/auth/kb-google-drive/callback';
  }
  // 운영환경: 같은 origin 사용
  return origin + '/api/auth/kb-google-drive/callback';
};
// 안내용 예시 URI (운영환경 기준)
const KB_DRIVE_REDIRECT_URI_EXAMPLE = 'https://acxion-on.etechcloud.co.kr/api/auth/kb-google-drive/callback';

interface KbGoogleDriveConfigModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: (email: string) => void;
}

export function KbGoogleDriveConfigModal({ open, onClose, onConnected }: KbGoogleDriveConfigModalProps) {
  const { showToast } = useToast();

  // 설정 단계: 1=가이드, 2=Client ID 입력, 3=계정 연결, 4=완료
  const [step, setStep] = useState<number>(1);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);

  // 연결 상태
  const [connected, setConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [connectError, setConnectError] = useState('');

  // 모달 열릴 때 기존 설정 로드
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    connectorApi.getKbGoogleDriveConfig()
      .then(cfg => {
        if (cfg?.connected) {
          setConnected(true);
          setConnectedEmail(cfg.connectedEmail || '');
          setClientId(cfg.clientId || '');
          setStep(4);
        } else if (cfg?.clientId) {
          setClientId(cfg.clientId || '');
          setStep(3);
        } else {
          setStep(1);
        }
      })
      .catch(() => {
        setStep(1);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  // OAuth 팝업 메시지 수신
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'KB_GOOGLE_DRIVE_CONNECTED') {
        setConnected(true);
        setConnectedEmail(e.data.email || '');
        setStep(4);
        setConnectError('');
        onConnected?.(e.data.email || '');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onConnected]);

  // Client ID/Secret 저장
  const handleSaveCredentials = async () => {
    if (!clientId.trim() || !clientSecret.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await connectorApi.saveKbGoogleDriveConfig(clientId.trim(), clientSecret.trim());
      setStep(3);
    } catch {
      setSaveError('저장 실패. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  // Google 계정 연결
  const handleConnect = async () => {
    setConnectError('');
    try {
      const url = await connectorApi.getKbGoogleDriveAuthUrl();
      const popup = window.open(url, 'kb-google-drive-auth', 'width=500,height=600');
      const timer = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(timer);
          try {
            const saved = await connectorApi.getKbGoogleDriveConfig();
            if (saved?.connected) {
              setConnected(true);
              setConnectedEmail(saved.connectedEmail || '');
              setStep(4);
              onConnected?.(saved.connectedEmail || '');
            }
          } catch { /* 무시 */ }
        }
      }, 500);
    } catch {
      setConnectError('인증 URL을 가져오는데 실패했습니다.');
    }
  };

  // 연결 해제
  const handleDisconnect = async () => {
    try {
      await connectorApi.disconnectKbGoogleDrive();
      setConnected(false);
      setConnectedEmail('');
      setStep(3);
      showToast('success', 'Google Drive 연결이 해제되었습니다.');
    } catch {
      showToast('error', '연결 해제 실패');
    }
  };

  // 푸터 버튼 렌더링
  const renderFooter = () => {
    if (loading) return null;

    return (
      <>
        {step === 1 && (
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        )}
        
        {step === 2 && (
          <>
            <Button color="gray" appearance="outline" onClick={() => setStep(1)}>이전</Button>
            <Button
              color="primary"
              disabled={!clientId || !clientSecret || saving}
              onClick={handleSaveCredentials}
            >
              {saving ? '저장 중...' : '저장 후 다음'}
            </Button>
          </>
        )}
        
        {step === 3 && (
          <Button color="gray" appearance="outline" onClick={() => setStep(2)}>이전</Button>
        )}
        
        {step === 4 && (
          <Button color="primary" onClick={onClose}>확인</Button>
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Google Drive 연결 설정"
      size="lg"
      footerLeft={<Button color="gray" appearance="outline" onClick={onClose}>닫기</Button>}
      footerRight={renderFooter()}
      style={{ width: 700, maxWidth: '90vw' }}
    >
      <div className="gmail-setup kb-drive-modal-content">
        {loading && (
          <div className="pipe-drive-loading">설정 정보 확인 중...</div>
        )}

        {/* ── Step 1: Google Cloud 설정 가이드 ── */}
        {!loading && step === 1 && (
          <div className="bedrock-guide">
            <div className="bedrock-guide-section node-guide-head">
              <div className="bedrock-guide-title">Google Cloud 프로젝트 설정</div>
              <p className="node-guide-desc">Google Drive API를 사용하려면 Google Cloud에서 앱을 등록해야 합니다.</p>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">1. 프로젝트 만들기</div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-1</span>
                <div>
                  <strong>콘솔 로그인</strong>
                  <p><a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> 접속 후 Google 계정 로그인</p>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-2</span>
                <div>
                  <strong>새 프로젝트 선택</strong>
                  <p>상단 프로젝트 이름 → 새 프로젝트 (기존 프로젝트 사용 가능)</p>
                </div>
              </div>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">2. Google Drive API 사용 설정</div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">2-1</span>
                <div>
                  <strong>라이브러리 열기</strong>
                  <p>왼쪽 메뉴 → API 및 서비스 → 라이브러리</p>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">2-2</span>
                <div>
                  <strong>API 검색 후 사용 설정</strong>
                  <p>검색창에 <code className="gmail-code">Google Drive API</code> 입력 → 사용 설정</p>
                </div>
              </div>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">3. OAuth 동의 화면 구성</div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">3-1</span>
                <div>
                  <strong>OAuth 동의 화면 열기</strong>
                  <p>왼쪽 메뉴 → OAuth 동의 화면</p>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">3-2</span>
                <div>
                  <strong>권한 범위 추가</strong>
                  <p>데이터 액세스 → 범위 추가 → <code className="gmail-code">drive</code> 검색 → 아래 2개 체크</p>
                  <ul className="node-scope-list">
                    <li><code className="gmail-code">drive.readonly</code> 파일 읽기</li>
                    <li><code className="gmail-code">drive.file</code> 파일 업로드</li>
                  </ul>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">3-3</span>
                <div>
                  <strong>테스트 사용자 추가</strong>
                  <p>인증 센터 → 테스트 사용자 → 사용자 추가 → 본인 Google 주소 입력</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Client ID / Secret 입력 ── */}
        {!loading && step === 2 && (
          <div className="bedrock-guide">
            <div className="bedrock-guide-section node-guide-head">
              <div className="bedrock-guide-title">OAuth 클라이언트 ID 생성</div>
              <p className="node-guide-desc">Google 계정 연결에 사용할 OAuth 클라이언트를 만들고 발급된 값을 입력합니다.</p>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">1. OAuth 클라이언트 만들기</div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-1</span>
                <div>
                  <strong>클라이언트 만들기</strong>
                  <p>왼쪽 메뉴 → 클라이언트 → OAuth 클라이언트 만들기</p>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-2</span>
                <div>
                  <strong>애플리케이션 정보</strong>
                  <p>유형: 웹 애플리케이션 · 이름: 자유 입력</p>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-3</span>
                <div className="node-guide-step-body">
                  <strong>승인된 리디렉션 URI 추가</strong>
                  <p>승인된 리디렉션 URI → URI 추가 → 아래 값을 복사하여 붙여넣기</p>
                  <div className="bedrock-ext-row node-copy-row">
                    <input className="input-control" type="text" value={KB_DRIVE_REDIRECT_URI_EXAMPLE} readOnly />
                    <button
                      type="button"
                      className="bedrock-copy-btn node-copy-btn"
                      title="복사"
                      onClick={() => { navigator.clipboard.writeText(KB_DRIVE_REDIRECT_URI_EXAMPLE); showToast('success', '리디렉션 URI 복사됨'); }}
                    ><CopyIcon /></button>
                  </div>
                </div>
              </div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">1-4</span>
                <div>
                  <strong>만들기 후 값 복사</strong>
                  <p>만들기 → 팝업에 표시되는 클라이언트 ID · 클라이언트 보안 비밀번호 복사</p>
                </div>
              </div>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">2. 클라이언트 ID 입력 <span className="required">*</span></div>
              <div className="input">
                <input type="text" className="input-control"
                  placeholder="123456789-xxx.apps.googleusercontent.com"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </div>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">3. 클라이언트 보안 비밀번호 입력 <span className="required">*</span></div>
              <div className="input">
                <input type="password" className="input-control"
                  placeholder="GOCSPX-xxxxxxxxxx"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                />
              </div>
              {saveError && <div className="node-save-error">{saveError}</div>}
            </div>
          </div>
        )}

        {/* ── Step 3: Google 계정 연결 ── */}
        {!loading && step === 3 && (
          <div className="bedrock-guide">
            <div className="bedrock-guide-section node-guide-head">
              <div className="bedrock-guide-title">Google 계정 연결</div>
              <p className="node-guide-desc">Google Drive에 접근할 계정으로 로그인하여 아래 권한을 허용합니다.</p>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">1. 허용되는 권한</div>
              <div className="node-permission-list">
                <div className="node-permission-item"><PlusIcon />새 파일 추가 감지</div>
                <div className="node-permission-item"><EditIcon />파일 수정 감지</div>
                <div className="node-permission-item"><UploadIcon />파일 업로드</div>
              </div>
            </div>

            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">2. 계정 연결</div>
              <div className="bedrock-guide-step">
                <span className="bedrock-guide-badge">2-1</span>
                <div className="node-guide-step-body">
                  <strong>Google 로그인</strong>
                  <p>아래 버튼을 클릭하면 Google 로그인 창이 열립니다. Google Drive에 접근할 계정으로 로그인해주세요.</p>
                  <button
                    type="button"
                    className="btn-google node-oauth-btn"
                    onClick={handleConnect}
                  >
                    <img src={googleDriveLogo} alt="" /> Google 계정으로 연결
                  </button>
                  {connectError && <div className="node-save-error" style={{ marginTop: 8 }}>{connectError}</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: 연결 완료 ── */}
        {!loading && step === 4 && connected && (
          <div className="bedrock-guide">
            <div className="bedrock-guide-section node-guide-head">
              <div className="bedrock-guide-title">Google Drive 연결 완료</div>
              <p className="node-guide-desc">Google Drive 계정이 연결되었습니다. 이제 파일을 가져올 수 있습니다.</p>
            </div>

            <div className="kb-account-card">
              <div className="kb-account-info">
                <span className="kb-account-icon">
                  <img src={googleDriveLogo} alt="" />
                </span>
                <div className="kb-account-text">
                  <strong>{connectedEmail}</strong>
                  <span>Google Drive 계정이 연결되어 있습니다</span>
                </div>
              </div>
              <div className="kb-account-actions">
                <Button color="gray" appearance="outline" size="sm" onClick={handleDisconnect}>
                  <RefreshIcon /> 연결 해제
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
